// The transaction-interaction graph: a point-in-time picture of how the open
// transactions overlap through the memory they touch. Transactions sit in a left
// column, the memory locations they read/write in a right column, and every read
// (blue), write (orange), or waited-on (dashed) address is an edge between them —
// a deliberately bipartite, graphviz-`rankdir=LR`-style layout rather than a
// force-directed one, so it is deterministic (no jitter between renders) and the
// shared cells that cause conflicts line up plainly in the middle.
//
// The model (playground/graph-model.ts) also flags the three things this view is
// for, which we render as accents on that base graph:
//   * a running txn that would abort — red node + red dashed stale read edges;
//   * a `retry`-parked txn — amber dashed node + its waited-on read edges;
//   * the write that will unpark a waiter — a green "wakes" write edge into the
//     contended location.
//
// Clicking a transaction focuses that thread in the debugger sidebar.

import { useMemo, useState } from "react";
import type { DebugSnapshot } from "../../src/backend/interp/debug.js";
import type { Store } from "../../src/backend/interp/store.js";
import type { TraceEvent } from "../../src/backend/interp/trace.js";
import { buildTxnGraph, type AddrNode, type EdgeKind, type TxnNode } from "../graph-model.js";
import type { ObjectTypes } from "../trace-layout.js";

interface GraphViewProps {
  snapshot: DebugSnapshot | null;
  store: Store | null;
  types?: ObjectTypes;
  /** The selected trace event, so a txn's own commit/abort row shows its known
   *  outcome (green / red) rather than a speculative prediction. */
  event?: TraceEvent | null;
  /** The node id (`txn:<id>` / `addr:<address>`) matching the trace's active
   *  lifeline, drawn as selected — the graph↔trace selection is kept in sync. */
  selectedNodeId?: string | null;
  /** Click a node: the parent maps it to the trace's lifeline selection (and, for
   *  a txn, focuses that thread in the debugger). */
  onSelectNode?(nodeId: string): void;
}

// --- Layout geometry (a fixed coordinate space; the container scrolls). -------
const PAD = 16;
const TXN_W = 148;
const TXN_H = 46;
const TXN_GAP = 16; // vertical gap between transaction boxes
const RETRY_GAP = 36; // taller gap under a ghost, leaving room for the retry arrow
const ADDR_H = 26;
const ADDR_GAP = 12; // vertical gap between location boxes
const COL_GAP = 128; // horizontal space between the two columns (room for edges)
const CHAR_W = 6.6; // ~monospace advance at 11px, for sizing location boxes
const ADDR_MIN_W = 84;
const ADDR_MAX_W = 240;

interface Placed<T> {
  node: T;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Geometry {
  txns: Placed<TxnNode>[];
  addrs: Placed<AddrNode>[];
  edges: {
    id: string;
    from: string;
    to: string;
    kind: EdgeKind;
    stale: boolean;
    wakes: boolean;
    d: string;
  }[];
  width: number;
  height: number;
}

function addrWidth(label: string): number {
  return Math.max(ADDR_MIN_W, Math.min(ADDR_MAX_W, Math.round(label.length * CHAR_W) + 20));
}

/** A left-to-right cubic between two node edges, bowed by the horizontal gap. */
function edgePath(x1: number, y1: number, x2: number, y2: number): string {
  const dx = Math.max(40, (x2 - x1) * 0.5);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

/** A top-to-bottom cubic, for the `retry` hand-off between two stacked txn boxes. */
function retryPath(x1: number, y1: number, x2: number, y2: number): string {
  const dy = Math.max(6, (y2 - y1) * 0.4);
  return `M ${x1} ${y1} C ${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
}

function layout(txnNodes: TxnNode[], addrNodes: AddrNode[], edgesIn: ReturnType<typeof buildTxnGraph>["edges"]): Geometry {
  const txnIndex = new Map(txnNodes.map((t, i) => [t.id, i]));

  // A `retry` edge links two stacked txn boxes (the model puts the ghost directly
  // above its successor), so the gap under the ghost is widened for the arrow.
  const retryGapAt = new Set<number>();
  for (const e of edgesIn) {
    if (e.kind !== "retry") continue;
    const a = txnIndex.get(e.from);
    const b = txnIndex.get(e.to);
    if (a !== undefined && b === (a as number) + 1) retryGapAt.add(a);
  }
  const txnY: number[] = [];
  let acc = 0;
  txnNodes.forEach((_, i) => {
    txnY.push(acc);
    acc += TXN_H + (i < txnNodes.length - 1 ? (retryGapAt.has(i) ? RETRY_GAP : TXN_GAP) : 0);
  });
  const txnColH = acc;

  // Order the location column by the average position of the transactions that
  // touch each address (a one-pass barycentric heuristic) so edges cross as
  // little as possible; ties fall back to address order for stability.
  const bary = new Map<string, number>();
  const seen = new Map<string, number>();
  for (const e of edgesIn) {
    if (e.kind === "retry") continue; // txn→txn, not part of the location ordering
    const ti = txnIndex.get(e.from);
    if (ti === undefined) continue;
    bary.set(e.to, (bary.get(e.to) ?? 0) + ti);
    seen.set(e.to, (seen.get(e.to) ?? 0) + 1);
  }
  const orderedAddrs = [...addrNodes].sort((a, b) => {
    const av = (bary.get(a.id) ?? 0) / Math.max(1, seen.get(a.id) ?? 1);
    const bv = (bary.get(b.id) ?? 0) / Math.max(1, seen.get(b.id) ?? 1);
    return av - bv || (a.addr < b.addr ? -1 : a.addr > b.addr ? 1 : 0);
  });

  const addrColH = orderedAddrs.length * ADDR_H + Math.max(0, orderedAddrs.length - 1) * ADDR_GAP;
  const bodyH = Math.max(txnColH, addrColH);

  const txnX = PAD;
  const addrW = Math.max(ADDR_MIN_W, ...orderedAddrs.map((a) => addrWidth(a.label)), ADDR_MIN_W);
  const addrX = PAD + TXN_W + COL_GAP;

  const txns: Placed<TxnNode>[] = txnNodes.map((node, i) => ({
    node,
    x: txnX,
    y: PAD + (bodyH - txnColH) / 2 + txnY[i]!,
    w: TXN_W,
    h: TXN_H,
  }));
  const addrs: Placed<AddrNode>[] = orderedAddrs.map((node, i) => ({
    node,
    x: addrX,
    y: PAD + (bodyH - addrColH) / 2 + i * (ADDR_H + ADDR_GAP),
    w: addrWidth(node.label),
    h: ADDR_H,
  }));

  const txnById = new Map(txns.map((p) => [p.node.id, p]));
  const addrById = new Map(addrs.map((p) => [p.node.id, p]));
  const edges = edgesIn
    .map((e) => {
      const s = txnById.get(e.from);
      if (!s) return null;
      // A `retry` hand-off runs downward between two txn boxes; every other edge
      // runs left-to-right from a txn to a location.
      if (e.kind === "retry") {
        const t = txnById.get(e.to);
        if (!t) return null;
        return { ...e, d: retryPath(s.x + s.w / 2, s.y + s.h, t.x + t.w / 2, t.y) };
      }
      const t = addrById.get(e.to);
      if (!t) return null;
      const x1 = s.x + s.w;
      const y1 = s.y + s.h / 2;
      const x2 = t.x;
      const y2 = t.y + t.h / 2;
      return { ...e, d: edgePath(x1, y1, x2, y2) };
    })
    .filter((e): e is NonNullable<typeof e> => e !== null);

  const width = addrX + addrW + PAD;
  const height = bodyH + PAD * 2;
  return { txns, addrs, edges, width, height };
}

/** Colour class for an edge, most-salient accent winning. */
function edgeClass(e: Geometry["edges"][number]): string {
  const base = `gv-edge gv-${e.kind}`;
  if (e.stale) return `${base} gv-stale`;
  if (e.wakes) return `${base} gv-wake`;
  return base;
}

/** The status tag shown under a transaction's label (empty when plain running).
 *  A known commit/abort outcome (this is the txn's own commit/abort event) wins
 *  over the speculative "will abort" prediction. */
function txnStatus(t: TxnNode): { text: string; cls: string } | null {
  // Actions this txn is taking at the selected event (commit/abort/lock/unlock/
  // retry) read as verbs and win over the resting state, so a txn on its own
  // `unlock` row says "unlock", not "holding · lock".
  // A ghost is the attempt a re-run replaced, and how it ended is the useful
  // label — never guess: "retried" only when it actually parked on a `retry`,
  // "aborted" when it aborted, and a neutral "replaced" if the snapshot didn't
  // retain which (it says the one thing that is certainly true).
  if (t.ghost) {
    if (t.outcome === "abort") return { text: "aborted", cls: "gv-t-abort" };
    if (t.parked === "retry") return { text: "retried", cls: "gv-t-parked" };
    return { text: "replaced", cls: "gv-t-parked" };
  }
  if (t.retryOf !== null) return { text: `re-run of #${t.retryOf}`, cls: "gv-t-parked" };
  if (t.outcome === "commit") return { text: "commit", cls: "gv-t-commit" };
  if (t.outcome === "abort") return { text: "abort", cls: "gv-t-abort" };
  if (t.action === "retry") return { text: "retry · park", cls: "gv-t-parked" };
  if (t.action === "orelse") return { text: "orElse · next branch", cls: "gv-t-parked" };
  if (t.action === "lock") return { text: "lock", cls: "gv-t-lock" };
  if (t.action === "unlock") return { text: "unlock", cls: "gv-t-lock" };
  if (t.willAbort) return { text: "will abort", cls: "gv-t-abort" };
  if (t.parked === "retry") return { text: "parked · retry", cls: "gv-t-parked" };
  if (t.parked === "lock") return { text: "waiting · lock", cls: "gv-t-parked" };
  if (t.parked === "lock-held") return { text: "holding · lock", cls: "gv-t-lock" };
  return null;
}

export function GraphView({
  snapshot,
  store,
  types,
  event,
  selectedNodeId,
  onSelectNode,
}: GraphViewProps) {
  const [hover, setHover] = useState<string | null>(null);

  const graph = useMemo(
    () => (snapshot && store ? buildTxnGraph(snapshot, store, types, event) : null),
    [snapshot, store, types, event]
  );
  const geom = useMemo(
    () => (graph ? layout(graph.txns, graph.addrs, graph.edges) : null),
    [graph]
  );

  if (!snapshot || !store) {
    return (
      <div id="graphview">
        <div className="gv-hint">Select a trace row to see how the open transactions interact.</div>
      </div>
    );
  }

  if (!graph || graph.txns.length === 0) {
    return (
      <div id="graphview">
        <div className="gv-hint">No open transactions at this point in time.</div>
      </div>
    );
  }

  // Which edges/nodes stay lit under the hover: a hovered node lights its own
  // incident edges and their far endpoints; nothing hovered lights everything.
  const lit = (edge: Geometry["edges"][number]): boolean =>
    hover === null || edge.from === hover || edge.to === hover;
  const litNode = (id: string): boolean =>
    hover === null ||
    id === hover ||
    geom!.edges.some((e) => (e.from === hover && e.to === id) || (e.to === hover && e.from === id));

  return (
    <div id="graphview">
      {/* The counts sit with the legend rather than in a header: the panel this
          lives in supplies the header, and two of them stacked reads as a mistake. */}
      <div className="gv-legend" aria-hidden="true">
        <span className="gv-count">
          {graph.txns.length} txn{graph.txns.length === 1 ? "" : "s"} · {graph.addrs.length} loc
          {graph.addrs.length === 1 ? "" : "s"}
        </span>
        <span className="gv-key gv-k-read">read</span>
        <span className="gv-key gv-k-write">write</span>
        <span className="gv-key gv-k-waiting">waiting</span>
        <span className="gv-key gv-k-lock">holds lock</span>
        <span className="gv-key gv-k-retry">retry → re-run</span>
        <span className="gv-key gv-k-stale">stale read → abort</span>
        <span className="gv-key gv-k-wake">write wakes waiter</span>
        <span className="gv-key gv-box gv-k-commit">commit</span>
        <span className="gv-key gv-box gv-k-abort">abort</span>
      </div>
      <div className="gv-canvas">
        <svg
          className={`gv-svg${hover ? " gv-hovering" : ""}`}
          width={geom!.width}
          height={geom!.height}
          viewBox={`0 0 ${geom!.width} ${geom!.height}`}
          role="img"
          aria-label="transaction interaction graph"
        >
          <defs>
            {/* One arrowhead, painted to match whatever stroke references it. */}
            <marker
              id="gv-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
            </marker>
          </defs>

          {/* Edges first, under the nodes. */}
          <g className="gv-edges">
            {geom!.edges.map((e) => (
              <path
                key={e.id}
                className={`${edgeClass(e)}${lit(e) ? "" : " gv-dim"}`}
                d={e.d}
                markerEnd="url(#gv-arrow)"
              >
                <title>
                  {e.stale
                    ? "stale read — committed over since this txn's snapshot (will abort)"
                    : e.wakes
                    ? "write to an address a parked txn is waiting on — will unpark it"
                    : e.kind}
                </title>
              </path>
            ))}
          </g>

          {/* Memory-location nodes. */}
          <g className="gv-addrs">
            {geom!.addrs.map((p) => {
              const cls = [
                "gv-addr",
                p.node.contended ? "gv-contended" : "",
                p.node.wakes ? "gv-wakes" : "",
                p.node.id === selectedNodeId ? "gv-sel" : "",
                onSelectNode ? "gv-click" : "",
                litNode(p.node.id) ? "" : "gv-dim",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <g
                  key={p.node.id}
                  className={cls}
                  transform={`translate(${p.x} ${p.y})`}
                  onMouseEnter={() => setHover(p.node.id)}
                  onMouseLeave={() => setHover(null)}
                  onClick={onSelectNode ? () => onSelectNode(p.node.id) : undefined}
                >
                  <rect width={p.w} height={p.h} rx="5" />
                  <text x={p.w / 2} y={p.h / 2} className="gv-addr-label">
                    {p.node.label}
                  </text>
                  <title>
                    {p.node.label} — {p.node.readers} reader{p.node.readers === 1 ? "" : "s"},{" "}
                    {p.node.writers} writer{p.node.writers === 1 ? "" : "s"}
                    {p.node.contended ? " · contended" : ""}
                  </title>
                </g>
              );
            })}
          </g>

          {/* Transaction nodes. */}
          <g className="gv-txns">
            {geom!.txns.map((p) => {
              const status = txnStatus(p.node);
              // A known commit/abort outcome wins over the parked/will-abort
              // styling: green for a committing txn, red for an aborting one.
              const committing = p.node.outcome === "commit";
              const aborting = p.node.outcome === "abort" || (!committing && p.node.willAbort);
              const blocked = !committing && !aborting;
              const cls = [
                "gv-txn",
                p.node.ghost ? "gv-ghost" : "",
                committing ? "gv-commit" : "",
                aborting ? "gv-abort" : "",
                // Blocked/waiting (retry, lock-wait) is a dashed node; holding its
                // locks pre-begin is a solid "has it" node, distinct from waiting.
                blocked && (p.node.parked === "retry" || p.node.parked === "lock") ? "gv-parked" : "",
                blocked && p.node.parked === "lock-held" ? "gv-holding" : "",
                p.node.acting ? "gv-acting" : "",
                p.node.id === selectedNodeId ? "gv-sel" : "",
                onSelectNode ? "gv-click" : "",
                litNode(p.node.id) ? "" : "gv-dim",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <g
                  key={p.node.id}
                  className={cls}
                  transform={`translate(${p.x} ${p.y})`}
                  onMouseEnter={() => setHover(p.node.id)}
                  onMouseLeave={() => setHover(null)}
                  onClick={onSelectNode ? () => onSelectNode(p.node.id) : undefined}
                >
                  <rect width={p.w} height={p.h} rx="7" />
                  <text x="10" y={status ? 16 : 19} className="gv-txn-id">
                    txn#{p.node.txnId}
                    {p.node.acting ? " ▸" : ""}
                  </text>
                  <text x="10" y={status ? 29 : 33} className="gv-txn-sub">
                    T{p.node.thread} · {p.node.entryFn}
                  </text>
                  {status && (
                    <text x="10" y={40} className={`gv-txn-status ${status.cls}`}>
                      {status.text}
                    </text>
                  )}
                  <title>
                    txn#{p.node.txnId} on thread T{p.node.thread} ({p.node.entryFn}) —{" "}
                    {p.node.readCount} read{p.node.readCount === 1 ? "" : "s"}, {p.node.writeCount} write
                    {p.node.writeCount === 1 ? "" : "s"}
                    {status ? ` · ${status.text}` : ""}
                  </title>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}
